import React, { useCallback, useRef, useState } from 'react';
import { StyleSheet } from 'react-native';
import type WebView from 'react-native-webview';
import {
  theme,
  useNoticeBannersStackContext,
  useResponsive,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import { CreditCardFormControlledContent } from './components/CreditCardForm.controlled';
import { defaultCreditCardForm } from './CreditCardForm.constants';
import type { FormProps } from './CreditCardForm.types';
import { useBraintreeMessages } from './hooks/Braintree.hooks';
import { useCreditCardTelemetry } from './hooks/CreditCardForm.hooks.telemetry';
import { FRAME_HEIGHT } from './hosted-fields/constants';
import { useBraintreeWebSource } from './hosted-fields/CreditCardForm.webView';
import {
  CreditCardFormHost,
  injectTokenizeRequest,
} from './hosted-fields/CreditCardFormHost';

export const CreditCardFormContent = ({
  vault,
  account,
  mode,
  isSaving,
  isDeleting,
  excludedFields = [],
  onSave,
  onEdit,
  onDelete,
}: FormProps) => {
  const isEditing = mode === 'edit';

  const { currentBreakpoint } = useResponsive();

  // ────── Controlled Form State ───────────────────────────────────────

  const [form, setForm] = useState({
    nickname: account?.nickname ?? defaultCreditCardForm.nickname,
    default: account?.isDefault ?? defaultCreditCardForm.default,
  });

  // ────── Form Submission Validation ──────────────────────────────────

  const hasChangedNickname = form.nickname !== account?.nickname;
  const hasChangedDefault = form.default !== account?.isDefault;
  const hasEdited = hasChangedNickname || hasChangedDefault;
  const canSubmitForm = !isEditing || hasEdited;

  // ────── Telemetry ────────────────────────────────────────────────────

  const {
    monitoredOnSave,
    monitoredOnEdit,
    monitoredOnFail,
    monitoredOnDelete,
  } = useCreditCardTelemetry({
    isEditing,
    onSave,
    onEdit,
    onDelete,
  });

  // ────── WebView Setup ───────────────────────────────────────────────

  const handleBraintreeSuccess = useCallback(
    (nonce: string) => {
      monitoredOnSave(form, nonce);
    },
    [monitoredOnSave, form],
  );

  const braintreeWebSource = useBraintreeWebSource(vault);
  const webViewRef = useRef<WebView>();
  const {
    isLoadingFields,
    isTokenizing,
    hasCardNumber,
    hasExpDate,
    hasCvv,
    hasPostalCode,
    handleBraintreeMessages,
  } = useBraintreeMessages({
    isEditing,
    onError: monitoredOnFail,
    onSuccess: handleBraintreeSuccess,
  });

  // ─────────────────────── Callbacks ──────────────────────────────────

  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { t } = useLocalizationContext();
  const submitForm = useCallback(() => {
    (async () => {
      if (isEditing) {
        monitoredOnEdit(form);

        return;
      }

      if (isLoadingFields) {
        addNoticeBanner({
          palette: 'caution',
          text: t('credit-card-form.error.submit.loading'),
        });

        return;
      }

      if (!hasCardNumber) {
        addNoticeBanner({
          palette: 'caution',
          text: t('credit-card-form.error.submit.number'),
        });

        return;
      }

      if (!hasExpDate) {
        addNoticeBanner({
          palette: 'caution',
          text: t('credit-card-form.error.submit.expiration'),
        });

        return;
      }

      if (!hasCvv) {
        addNoticeBanner({
          palette: 'caution',
          text: t('credit-card-form.error.submit.cvv'),
        });

        return;
      }

      if (!hasPostalCode) {
        addNoticeBanner({
          palette: 'caution',
          text: t('credit-card-form.error.submit.zipCode'),
        });

        return;
      }

      injectTokenizeRequest(webViewRef);
    })();
  }, [
    form,
    isEditing,
    isLoadingFields,
    hasCardNumber,
    hasExpDate,
    hasCvv,
    hasPostalCode,
    t,
    addNoticeBanner,
    monitoredOnEdit,
  ]);

  return (
    <CreditCardFormControlledContent
      account={account}
      isSaving={Boolean(isSaving) || isTokenizing}
      mode={mode}
      isDeleting={isDeleting}
      isLoadingFields={isLoadingFields}
      canSubmitForm={canSubmitForm}
      form={form}
      excludedFields={excludedFields}
      fadeDuration={0}
      onFormChange={setForm}
      onSubmitForm={submitForm}
      onDelete={monitoredOnDelete}
    >
      {isEditing ? null : (
        <CreditCardFormHost
          ref={webViewRef}
          isLoadingFields={isLoadingFields}
          source={braintreeWebSource}
          onMessage={handleBraintreeMessages}
          style={styles.webView}
          containerStyle={[
            styles.container,
            currentBreakpoint.isXS && styles.containerXS,
            isLoadingFields && styles.loadingFields,
          ]}
        />
      )}
    </CreditCardFormControlledContent>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: FRAME_HEIGHT,
    maxHeight: FRAME_HEIGHT,
    backgroundColor: theme.colors.OATMEAL,
    paddingHorizontal: theme.spacing['4'],
  },
  containerXS: {
    paddingHorizontal: theme.spacing['2'],
  },
  // for web views to function properly on Android, their container must be at least 1x1 in size
  loadingFields: {
    flex: 0,
    width: 1,
    height: 1,
  },
  webView: {
    backgroundColor: theme.colors.OATMEAL,
  },
});
